import axios from 'axios';

const ENDPOINT = 'etherscan/casebuddy_lite_batch.php';
const STORAGE_PREFIX = 'pathtracer.v2.batch.lease.';

function token() {
  return String(localStorage.getItem('token') || '').trim();
}

function authHeaders() {
  const jwt = token();
  return jwt
    ? {
        Authorization: `Bearer ${jwt}`,
        'Content-Type': 'application/json',
      }
    : {
        'Content-Type': 'application/json',
      };
}

function uuidV4() {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID();
  }

  const bytes = new Uint8Array(16);
  crypto.getRandomValues(bytes);
  bytes[6] = (bytes[6] & 0x0f) | 0x40;
  bytes[8] = (bytes[8] & 0x3f) | 0x80;
  const hex = Array.from(bytes, (value) => value.toString(16).padStart(2, '0')).join('');
  return [
    hex.slice(0, 8),
    hex.slice(8, 12),
    hex.slice(12, 16),
    hex.slice(16, 20),
    hex.slice(20),
  ].join('-');
}

function storageKey(batchUuid) {
  return `${STORAGE_PREFIX}${String(batchUuid || '').toLowerCase()}`;
}

function readLease(batchUuid) {
  try {
    const raw = sessionStorage.getItem(storageKey(batchUuid));
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (!parsed || !parsed.runner_session_id || !parsed.lease_token) return null;
    return parsed;
  } catch {
    return null;
  }
}

function writeLease(batchUuid, lease) {
  if (!lease?.runner_session_id || !lease?.lease_token) return;
  sessionStorage.setItem(storageKey(batchUuid), JSON.stringify({
    runner_session_id: lease.runner_session_id,
    lease_token: lease.lease_token,
    stored_at: Date.now(),
  }));
}

function clearLease(batchUuid) {
  try {
    sessionStorage.removeItem(storageKey(batchUuid));
  } catch {
    // Session-storage cleanup is best effort only.
  }
}

async function post(action, payload = {}) {
  const response = await axios.post(
    ENDPOINT,
    {
      action,
      ...payload,
    },
    {
      headers: authHeaders(),
    }
  );

  return response?.data || {};
}

async function createBatch(payload) {
  return post('create', payload);
}

async function getBatch(batchUuid, includeItems = true) {
  return post('get', {
    batch_uuid: batchUuid,
    include_items: includeItems,
  });
}

async function listBatches(limit = 20) {
  return post('list', { limit });
}

async function acquireLease(batchUuid) {
  const existing = readLease(batchUuid);
  const runnerSessionId = existing?.runner_session_id || uuidV4();

  try {
    const data = await post('acquire_lease', {
      batch_uuid: batchUuid,
      runner_session_id: runnerSessionId,
      ...(existing?.lease_token ? { lease_token: existing.lease_token } : {}),
    });

    writeLease(batchUuid, {
      runner_session_id: data.runner_session_id || runnerSessionId,
      lease_token: data.lease_token || existing?.lease_token,
    });

    return data;
  } catch (error) {
    const reason = String(error?.response?.data?.reason_code || '');
    if (['batch_lease_expired', 'batch_lease_mismatch'].includes(reason)) {
      clearLease(batchUuid);
    }
    throw error;
  }
}

function leasePayload(batchUuid) {
  const lease = readLease(batchUuid);
  if (!lease) {
    throw new Error('The batch processor lease is not available in this tab.');
  }
  return {
    batch_uuid: batchUuid,
    runner_session_id: lease.runner_session_id,
    lease_token: lease.lease_token,
  };
}

async function ensureLease(batchUuid) {
  const existing = readLease(batchUuid);
  if (existing) {
    try {
      return await acquireLease(batchUuid);
    } catch (error) {
      const reason = String(error?.response?.data?.reason_code || '');
      if (reason === 'batch_lease_active_elsewhere') throw error;
    }
  }
  return acquireLease(batchUuid);
}

async function heartbeat(batchUuid) {
  return post('heartbeat', leasePayload(batchUuid));
}

async function claimNext(batchUuid) {
  return post('claim_next', leasePayload(batchUuid));
}

async function advance(batchUuid) {
  return post('advance', leasePayload(batchUuid));
}

async function pause(batchUuid) {
  const result = await post('pause', leasePayload(batchUuid));
  clearLease(batchUuid);
  return result;
}

async function cancelPending(batchUuid) {
  const result = await post('cancel_pending', leasePayload(batchUuid));
  clearLease(batchUuid);
  return result;
}

function batchWindowName(batchUuid) {
  return `pathtracer-v2-batch-${String(batchUuid || '').toLowerCase()}`;
}

function requestRouteQuery(batchUuid, item, nextAction = 'start') {
  const action = nextAction === 'resume'
    ? 'resume'
    : nextAction === 'monitor'
      ? 'monitor'
      : 'start';

  return {
    action,
    batch: batchUuid,
    batch_item: String(item?.id || ''),
  };
}

export default {
  ENDPOINT,
  createBatch,
  getBatch,
  listBatches,
  acquireLease,
  ensureLease,
  heartbeat,
  claimNext,
  advance,
  pause,
  cancelPending,
  readLease,
  clearLease,
  batchWindowName,
  requestRouteQuery,
  createUuid: uuidV4,
};
